import React, { useState, useEffect } from 'react';
import { X, Save, Copy } from 'lucide-react';
import './CreateProviderModal.css';

const CreateProviderModal = ({ onClose, onCreateProvider, providerToDuplicate }) => {
    const initialProviderData = {
        username: '',
        name: '',
        email: '',
        phone: '',
        description: '',
        type: 'service', // default
        active: false,
        categories: [],
        subcategories: [],
        locations: [],
        keywords: [],
        links: []
    };

    const [providerData, setProviderData] = useState(initialProviderData);
    const [errors, setErrors] = useState({});
    const [categories, setCategories] = useState('');
    const [subcategories, setSubcategories] = useState('');
    const [locations, setLocations] = useState('');
    const [keywords, setKeywords] = useState('');
    const [links, setLinks] = useState('');
    const [isServiceType, setIsServiceType] = useState(true);

    // Si hay un proveedor para duplicar, cargamos sus datos
    useEffect(() => {
        if (providerToDuplicate) {
            const { username, ...restData } = providerToDuplicate;

            setProviderData({
                ...restData,
                username: `${username}_copia`,
                active: false // Los duplicados se crean inactivos por defecto
            });

            setCategories(restData.categories?.join(', ') || '');
            setSubcategories(restData.subcategories?.join(', ') || '');
            setLocations(restData.locations?.join(', ') || '');
            setKeywords(restData.keywords?.join(', ') || '');

            // Convertir links a formato de texto
            if (restData.links && restData.links.length > 0) {
                const linksText = restData.links.map(link => link.url).join('\n');
                setLinks(linksText);
            }

            setIsServiceType(restData.type === 'service');
        }
    }, [providerToDuplicate]);

    // Validar campos
    const validateForm = () => {
        const newErrors = {};

        if (!providerData.username) newErrors.username = 'El nombre de usuario es obligatorio';
        if (!providerData.name) newErrors.name = 'El nombre es obligatorio';
        if (!providerData.email) newErrors.email = 'El email es obligatorio';
        if (!providerData.type) newErrors.type = 'El tipo es obligatorio';

        // Validar formato de email
        if (providerData.email && !/\S+@\S+\.\S+/.test(providerData.email)) {
            newErrors.email = 'El formato del email no es válido';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Manejar cambio en campos de texto
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setProviderData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Manejar cambio en el tipo (service/products)
    const handleTypeChange = (e) => {
        const { value } = e.target;
        setProviderData(prev => ({
            ...prev,
            type: value
        }));
        setIsServiceType(value === 'service');
    };

    // Manejar cambio en checkbox
    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setProviderData(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    // Procesar listas de texto a arrays
    const processTextToArray = (text) => {
        if (!text.trim()) return [];
        return text.split(',').map(item => item.trim()).filter(item => item);
    };

    // Procesar links
    const processLinksToArray = (text) => {
        if (!text.trim()) return [];
        return text.split('\n')
            .map(url => url.trim())
            .filter(url => url)
            .map(url => ({ url }));
    };

    // Manejar envío del formulario
    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        // Procesar datos de arrays
        const finalData = {
            ...providerData,
            categories: processTextToArray(categories),
            subcategories: processTextToArray(subcategories),
            locations: processTextToArray(locations),
            keywords: processTextToArray(keywords),
            links: processLinksToArray(links)
        };

        onCreateProvider(finalData);
    };

    return (
        <div className="provider-modal-backdrop">
            <div className="create-provider-modal">
                <div className="modal-header">
                    <h2>
                        {providerToDuplicate
                            ? <><Copy size={20} className="icon-margin-right" /> Duplicar Proveedor</>
                            : 'Crear Nuevo Proveedor'
                        }
                    </h2>
                    <button
                        className="close-modal-btn"
                        onClick={onClose}
                        title="Cerrar"
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="modal-body">
                    <form onSubmit={handleSubmit} className="create-provider-form">
                        <div className="form-grid">
                            {/* Datos básicos */}
                            <div className="form-section">
                                <h3>Información Básica</h3>

                                <div className="form-group">
                                    <label htmlFor="username">
                                        Username*
                                        {errors.username && <span className="error-text">{errors.username}</span>}
                                    </label>
                                    <input
                                        type="text"
                                        id="username"
                                        name="username"
                                        value={providerData.username}
                                        onChange={handleInputChange}
                                        className={errors.username ? 'input-error' : ''}
                                        placeholder="username_sin_espacios"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="name">
                                        Nombre*
                                        {errors.name && <span className="error-text">{errors.name}</span>}
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={providerData.name}
                                        onChange={handleInputChange}
                                        className={errors.name ? 'input-error' : ''}
                                        placeholder="Nombre completo"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="email">
                                        Email*
                                        {errors.email && <span className="error-text">{errors.email}</span>}
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={providerData.email}
                                        onChange={handleInputChange}
                                        className={errors.email ? 'input-error' : ''}
                                        placeholder="email@ejemplo.com"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="phone">Teléfono</label>
                                    <input
                                        type="text"
                                        id="phone"
                                        name="phone"
                                        value={providerData.phone || ''}
                                        onChange={handleInputChange}
                                        placeholder="+54 11 1234-5678"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="description">Descripción</label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        value={providerData.description || ''}
                                        onChange={handleInputChange}
                                        rows="3"
                                        placeholder="Descripción del proveedor"
                                    ></textarea>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="type">
                                        Tipo de Proveedor*
                                        {errors.type && <span className="error-text">{errors.type}</span>}
                                    </label>
                                    <select
                                        id="type"
                                        name="type"
                                        value={providerData.type}
                                        onChange={handleTypeChange}
                                        className={errors.type ? 'input-error' : ''}
                                    >
                                        <option value="service">Servicios</option>
                                        <option value="products">Productos</option>
                                    </select>
                                </div>

                                <div className="form-group checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="active"
                                            checked={providerData.active || false}
                                            onChange={handleCheckboxChange}
                                        />
                                        Activar proveedor inmediatamente
                                    </label>
                                </div>
                            </div>

                            {/* Datos específicos según tipo */}
                            <div className="form-section">
                                <h3>Datos Específicos</h3>

                                {isServiceType ? (
                                    <>
                                        <div className="form-group">
                                            <label htmlFor="document">Documento</label>
                                            <input
                                                type="text"
                                                id="document"
                                                name="document"
                                                value={providerData.document || ''}
                                                onChange={handleInputChange}
                                                placeholder="DNI, CUIT, etc."
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="documentName">Nombre en Documento</label>
                                            <input
                                                type="text"
                                                id="documentName"
                                                name="documentName"
                                                value={providerData.documentName || ''}
                                                onChange={handleInputChange}
                                                placeholder="Nombre como aparece en el documento"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="timetable">Horario</label>
                                            <input
                                                type="text"
                                                id="timetable"
                                                name="timetable"
                                                value={providerData.timetable || ''}
                                                onChange={handleInputChange}
                                                placeholder="Lun-Vie: 9-18hs"
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="form-group">
                                            <label htmlFor="cuit">CUIT</label>
                                            <input
                                                type="text"
                                                id="cuit"
                                                name="cuit"
                                                value={providerData.cuit || ''}
                                                onChange={handleInputChange}
                                                placeholder="XX-XXXXXXXX-X"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="timetable">Horario</label>
                                            <input
                                                type="text"
                                                id="timetable"
                                                name="timetable"
                                                value={providerData.timetable || ''}
                                                onChange={handleInputChange}
                                                placeholder="Lun-Vie: 9-18hs"
                                            />
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Categorías y ubicaciones */}
                            <div className="form-section wide-section">
                                <h3>Categorías y Ubicaciones</h3>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="categories">Categorías (separadas por comas)</label>
                                        <input
                                            type="text"
                                            id="categories"
                                            value={categories}
                                            onChange={(e) => setCategories(e.target.value)}
                                            placeholder="Categoría1, Categoría2"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="subcategories">Subcategorías (separadas por comas)</label>
                                        <input
                                            type="text"
                                            id="subcategories"
                                            value={subcategories}
                                            onChange={(e) => setSubcategories(e.target.value)}
                                            placeholder="Subcategoría1, Subcategoría2"
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="locations">Ubicaciones (separadas por comas)</label>
                                    <input
                                        type="text"
                                        id="locations"
                                        value={locations}
                                        onChange={(e) => setLocations(e.target.value)}
                                        placeholder="Ubicación1, Ubicación2"
                                    />
                                </div>
                            </div>

                            {/* Datos adicionales */}
                            <div className="form-section wide-section">
                                <h3>Información Adicional</h3>

                                <div className="form-group">
                                    <label htmlFor="keywords">Palabras clave (separadas por comas)</label>
                                    <input
                                        type="text"
                                        id="keywords"
                                        value={keywords}
                                        onChange={(e) => setKeywords(e.target.value)}
                                        placeholder="palabra1, palabra2"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="links">Enlaces (uno por línea)</label>
                                    <textarea
                                        id="links"
                                        value={links}
                                        onChange={(e) => setLinks(e.target.value)}
                                        rows="3"
                                        placeholder="https://ejemplo1.com&#10;https://ejemplo2.com"
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="button" className="cancel-btn" onClick={onClose}>
                                <X size={18} className="icon-margin-right" />
                                Cancelar
                            </button>
                            <button type="submit" className="save-btn">
                                <Save size={18} className="icon-margin-right" />
                                {providerToDuplicate ? 'Duplicar Proveedor' : 'Crear Proveedor'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateProviderModal;
